import { useEffect, useState } from "react";
import { CatalogPlan, TierId } from "../config/tierDisplay";

interface UsePlanCatalogResult {
	catalog: CatalogPlan[];
	planFor: (tier: TierId) => CatalogPlan | undefined;
	loading: boolean;
	error: string | null;
}

/**
 * Fetches the active plan catalog from /api/plan-catalog. Used by both the
 * public pricing page and the signup right-panel order summary so they
 * share one source of truth for Stripe price IDs and amounts.
 */
export function usePlanCatalog(): UsePlanCatalogResult {
	const [catalog, setCatalog] = useState<CatalogPlan[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let mounted = true;
		(async () => {
			try {
				const res = await fetch(
					`${process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN}/api/plan-catalog`,
				);
				if (!res.ok) {
					throw new Error(`HTTP ${res.status}`);
				}
				const data = await res.json();
				const plans = (data.Data?.plans ||
					data.plans ||
					[]) as CatalogPlan[];
				if (mounted) setCatalog(plans);
			} catch (err: any) {
				console.error("usePlanCatalog failed:", err);
				if (mounted) setError(err.message || "Failed to load pricing");
			} finally {
				if (mounted) setLoading(false);
			}
		})();
		return () => {
			mounted = false;
		};
	}, []);

	const planFor = (tier: TierId): CatalogPlan | undefined =>
		catalog.find((p) => p.tier === tier);

	return { catalog, planFor, loading, error };
}
